"use client"

import { observer } from "@legendapp/state/react"
import { Avatar, Group, Stack, Text } from "@mantine/core"
import { Spotlight } from "@mantine/spotlight"
import { IconPlayerPlay, IconTrash, IconUsers } from "@tabler/icons-react"
import { useMemo } from "react"
import { usePowerDialerSelection } from "@/hooks/usePowerDialerSelection"
import { showNotification } from "@/lib/notifications"
import { usePowerDialerStore } from "@/stores/powerDialer/store"
import { headerSearchSpotlight } from "./HeaderSearchSpotlight"
import classes from "./HeaderSearchSpotlightActions.module.css"
import { applySequenceCallSimulationCommand, applySequenceCallSimulationPreset } from "@/features/PowerDialer/dev/sequenceCallSimulationDev.store"
import { isSequenceSimulationDevEnabled, parseSequenceCallSimulationCommand } from "@/features/PowerDialer/dev/sequenceCallSimulationDev.helpers"

interface Props {
  query: string
}

export const HeaderSearchSpotlightSequenceSimDevActions = observer(function HeaderSearchSpotlightSequenceSimDevActions({ query }: Props) {
  const { powerDialerTargetType } = usePowerDialerSelection()
  const isSequenceTarget = powerDialerTargetType === "sequence"
  const dialerRows = usePowerDialerStore((state) => state.cachedDialerListItems)

  const parsedCommand = useMemo(() => parseSequenceCallSimulationCommand(query), [query])

  if (!isSequenceSimulationDevEnabled()) return null

  const runPreset = (preset: "first-login" | "wave" | "clear") => {
    if (!isSequenceTarget) {
      showNotification({
        type: "warning",
        title: "Sequence simulation unavailable",
        message: "Switch the PowerDialer to a sequence target before running sequence simulation commands.",
      })
      return
    }

    const result = applySequenceCallSimulationPreset(preset, dialerRows)

    showNotification({
      type: result.ok ? "success" : "error",
      title: result.ok ? "Sequence simulation updated" : "Sequence simulation failed",
      message: result.message,
    })

    if (result.ok) {
      headerSearchSpotlight.close()
    }
  }

  const runParsedCommand = () => {
    if (!isSequenceTarget) {
      showNotification({
        type: "warning",
        title: "Sequence simulation unavailable",
        message: "Switch the PowerDialer to a sequence target before running sequence simulation commands.",
      })
      return
    }

    const result = applySequenceCallSimulationCommand(query, dialerRows)

    showNotification({
      type: result.ok ? "success" : "error",
      title: result.ok ? "Sequence simulation updated" : "Sequence simulation failed",
      message: result.message,
    })

    if (result.ok) {
      headerSearchSpotlight.close()
    }
  }

  return (
    <>
      <Spotlight.Action
        classNames={{
          action: classes.container,
        }}
        onClick={() => runPreset("first-login")}
      >
        <Group wrap="nowrap" w="100%">
          <Avatar size="md" color="teal" classNames={{ placeholder: classes.icon }}>
            <IconUsers size={16} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={550} className={classes.text}>
              /seq sim first-login
            </Text>
            <Text size="xs" c="dimmed" className={classes.subtext}>
              Simulate initial login where multiple users are already on calls.
            </Text>
          </Stack>
        </Group>
      </Spotlight.Action>

      <Spotlight.Action
        classNames={{
          action: classes.container,
        }}
        onClick={() => runPreset("wave")}
      >
        <Group wrap="nowrap" w="100%">
          <Avatar size="md" color="cyan" classNames={{ placeholder: classes.icon }}>
            <IconUsers size={16} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={550} className={classes.text}>
              /seq sim wave
            </Text>
            <Text size="xs" c="dimmed" className={classes.subtext}>
              Simulate a wave where first contacts are progressively taken by teammates.
            </Text>
          </Stack>
        </Group>
      </Spotlight.Action>

      <Spotlight.Action
        classNames={{
          action: classes.container,
        }}
        onClick={() => runPreset("clear")}
      >
        <Group wrap="nowrap" w="100%">
          <Avatar size="md" color="red" classNames={{ placeholder: classes.icon }}>
            <IconTrash size={16} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={550} className={classes.text}>
              /seq sim clear
            </Text>
            <Text size="xs" c="dimmed" className={classes.subtext}>
              Clear all simulated assignments and return to live data only.
            </Text>
          </Stack>
        </Group>
      </Spotlight.Action>

      {parsedCommand && parsedCommand.type === "freeform" && (
        <Spotlight.Action
          classNames={{
            action: classes.container,
          }}
          onClick={runParsedCommand}
        >
          <Group wrap="nowrap" w="100%">
            <Avatar size="md" color="blue" classNames={{ placeholder: classes.icon }}>
              <IconPlayerPlay size={16} />
            </Avatar>
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Text size="md" fw={550} className={classes.text}>
                Run: {query.trim()}
              </Text>
              <Text size="xs" c="dimmed" className={classes.subtext}>
                Execute parsed free-form sequence simulation command.
              </Text>
            </Stack>
          </Group>
        </Spotlight.Action>
      )}
    </>
  )
})
